/**
 * PLAYER_BETTING (cee-lo) — sidebet phase. Everyone (including the
 * upcoming shooter) can place sidebets on the next roll. No banker
 * special case: all wallets open, all instructions identical.
 *
 * Available sidebets depend on whether the banker has already rolled.
 * Pre-banker-roll only the banker's-point sidebets are uncertain
 * (4+ / 3-); post-banker-roll the full set is open. Banner copy is
 * re-rendered in onUpdate because `update.bankerHasRolled` flips
 * mid-state when the banker's roll lands.
 *
 * Lock-in button is visible ONLY in betting states. Set true ONCE in
 * onEnter — calling it in onUpdate would kill the entry tween and
 * re-run the intro animation every game:update tick.
 *
 * The instruction banner is title-only here. The subtitle that listed
 * the sidebet options sat in the same strip the sidebet zones grow
 * into — with 3+ zones placed it overlapped the bills and was
 * unreadable. The hex selector buttons carry the option info instead.
 */

import type { GameUpdate } from '@sdl/shared';
import { CeeloState } from '@sdl/shared';
import { State } from '../State.js';

export class PlayerBettingState extends State {
  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('betting');
    game.countdownOverlay?.stop();

    // Wallet open for everyone — no active-player special case here.
    await game.enterBettingMode({
      open: true,
      animated: update.animated,
      previousState: update.previousState,
    });

    // Lock-in button enters once, only in onEnter.
    game.bettingHUD3D?.setLockInVisible(true);
    // Lock in the sidebet selector options from the authoritative update
    // (pre-banker-roll → 4+/3-; post-roll → +bank wins/loses).
    game.primeSidebetOptions(update);

    // Title-only banner — subtitle stays hidden (see header comment).
    game.instructionBanner?.setSubtitleVisible(false);
    this.showInstruction(update);

    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    // bankerHasRolled flips mid-state when the banker's roll lands —
    // re-prime the sidebet options (bank wins/loses open up post-roll).
    this.game.primeSidebetOptions(update);

    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onExit(update: GameUpdate): Promise<void> {
    // Clear the BETTING activity pill — leaving the sidebet window.
    this._clearBettingActivity();

    // NO dice pre-stage here (unlike craps): the rolling state's onEnter
    // calls _showOpponentReadyDice AFTER exitBettingMode resolves, so
    // spectators see the roller's dice appear as the camera lands — the
    // same rhythm as the roller seeing their own dice spawn on
    // authorization. Pre-staging put the dice on-screen during the
    // betting→roll hold, which read as a frozen pause.

    // A fade that completes a sidebet zone can ride the SAME update that
    // ends the window (the last lock-in and the pot-completing fade land
    // together). Hold the transition until the fader's bills finish
    // dropping and the locked zone consolidates — otherwise the rolling
    // state's exitBettingMode pans the camera and wipes the bills
    // mid-drop (same race as craps heads-up FADING → COME_OUT). Resolves
    // immediately when nothing is pending; settle-guaranteed inside.
    if (update.animated
        && (update.gameState === CeeloState.BANKER_ROLL
          || update.gameState === CeeloState.PLAYER_ROLL)) {
      await this.game.bettingController?.waitForLockedZoneTeardown();
    }
  }

  private showInstruction(update: GameUpdate): void {
    const text = update.bankerHasRolled
      ? 'Place a sidebet: choose Bank Wins, Bank Loses, Point 4+ or Point 3-'
      : "Place a sidebet on the banker's point: Point 4+ or Point 3-";
    this.game.instructionBanner?.show({ title: 'Sidebetting', text }, update.animated);
  }
}
